import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import styles from '../PdfRenderer.module.scss';
import { serializeSelection } from '../helpers/textOffset';
import { renderHighlight } from '../helpers/highlight';
import type { Highlight, HighlightGeometry, ViewerMode, ContractorNotePermissions } from '../types';
import type { ModelAction } from '../../../../../types/responses/ModelAction';
import { AnnotationsSidebar } from './AnnotationsSidebar';
import type { CommentsPanelProps } from './AnnotationsSidebar';
import { NotePopup } from './NotePopup';
import { PendingAnnotationModals } from './PendingAnnotationModals';
import { WordToolbar } from './WordToolbar';
import { DocumentSearchBar } from './DocumentSearchBar';
import { useWordViewerSearch } from '../hooks/useWordViewerSearch';
import { useSidebarResize } from '../hooks/useSidebarResize';
import { usePopupManager } from '../hooks/usePopupManager';
import { useNoteCodes } from '../hooks/useNoteCodes';
import { keepUnresolvedFailedHighlights } from '../helpers/failedHighlights';

interface ActivePopup {
    id: string;
    x: number;
    y: number;
    yBottom: number;
    pinned: boolean;
}

interface WordViewerProps {
    // Обязательный и БЕЗ дефолта — см. комментарий к ViewerMode в types/index.ts
    mode: ViewerMode;
    commentsPanel?: React.ComponentType<CommentsPanelProps> | null;
    contractorPermissions?: Record<number, ContractorNotePermissions>;
    onContractorComplete?: (noteId: number, comment: string) => Promise<void>;
    onContractorCancelCompletion?: (noteId: number) => Promise<void>;
    noteLinkHref?: (noteId: number) => string | null;
    url: string;
    initialHighlights?: Highlight[];
    onHighlightsChange?: (highlights: Highlight[]) => void | Promise<void>;
    workflowInstanceId?: number;
    noteObjectId?: number;
    workflowInstanceAttachmentId?: number;
    onLinkToExisting?: (geometry: HighlightGeometry, noteId: number) => Promise<void>;
    onDeleteNote?: (noteId: number) => Promise<void>;
    initialScrollToNoteId?: number;
    noteActions?: ModelAction[];
    onApproveNote?: (noteId: number) => Promise<void>;
    onCancelNote?: (noteId: number, comment: string) => Promise<void>;
    onReturnToExpert?: (noteId: number, comment: string) => Promise<void>;
    onSendForApproval?: (noteId: number) => Promise<void>;
    onRevokeDecision?: (noteId: number) => Promise<void>;
    onConfirmCompletion?: (noteId: number) => Promise<void>;
    onConfirmNotCompleted?: (noteId: number, comment: string) => Promise<void>;
    onCancelByInitiator?: (noteId: number, comment: string) => Promise<void>;
    onCancelCompletion?: (noteId: number) => Promise<void>;
    onCompleteCompletedGIP?: (noteId: number, comment: string) => Promise<void>;
    onRequestAssignToExpert?: (noteId: number) => void;
    workflowId?: number;
    onDownload?: () => void;
    /** Стартовать со свёрнутым сайдбаром замечаний (узкие split-view). */
    initialSidebarCollapsed?: boolean;
}

export function WordViewer({
    mode,
    commentsPanel,
    contractorPermissions,
    onContractorComplete,
    onContractorCancelCompletion,
    noteLinkHref,
    url,
    initialHighlights,
    onHighlightsChange,
    workflowInstanceId,
    noteObjectId,
    workflowInstanceAttachmentId,
    onLinkToExisting,
    onDeleteNote,
    initialScrollToNoteId,
    noteActions,
    onApproveNote,
    onCancelNote,
    onReturnToExpert,
    onSendForApproval,
    onRevokeDecision,
    onConfirmCompletion,
    onConfirmNotCompleted,
    onCancelByInitiator,
    onCancelCompletion,
    onCompleteCompletedGIP,
    onRequestAssignToExpert,
    workflowId,
    onDownload,
    initialSidebarCollapsed = false,
}: WordViewerProps) {
    const canAnnotate = mode === 'edit';
    const [highlights, setHighlights] = useState<Highlight[]>([]);
    const [activePopup, setActivePopup] = useState<ActivePopup | null>(null);

    const onCommentsCountChange = useCallback((noteId: number, count: number) => {
        setHighlights(prev => prev.map(h =>
            h.detail?.noteId === noteId && h.detail
                ? { ...h, detail: { ...h.detail, commentsCount: count } }
                : h
        ));
    }, []);
    const [textMode, setTextMode] = useState(false);
    const [pendingSelection, setPendingSelection] = useState<{ startOffset: number; endOffset: number; text: string } | null>(null);
    const [pendingComment, setPendingComment] = useState('');
    const [pendingIsCritical, setPendingIsCritical] = useState(false);
    const [pendingCodeId, setPendingCodeId] = useState<number | undefined>(undefined);
    const [editingHighlight, setEditingHighlight] = useState<{ id: string; note: string } | null>(null);
    const [editingComment, setEditingComment] = useState('');
    const [editingIsCritical, setEditingIsCritical] = useState(false);
    const [editingCodeId, setEditingCodeId] = useState<number | undefined>(undefined);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showLinkScreen, setShowLinkScreen] = useState(false);
    const [linkedNote, setLinkedNote] = useState<{ id: number; title: string } | null>(null);
    const [docReady, setDocReady] = useState(false);
    const [docError, setDocError] = useState<string | null>(null);
    const [, setContainerWidth] = useState(0);

    const wordContentRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const searchOverlayRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const flashElRef = useRef<HTMLElement[]>([]);
    const initialScrollDoneRef = useRef(false);
    const highlightsRef = useRef<Highlight[]>([]);
    highlightsRef.current = highlights;
    const onHighlightsChangeRef = useRef(onHighlightsChange);
    onHighlightsChangeRef.current = onHighlightsChange;

    const { sidebarRef, sidebarCollapsed, setSidebarCollapsed, sidebarWidth, handleDividerDown } =
        useSidebarResize({ wrapperRef, setContainerWidth, initialSidebarCollapsed });

    const { cancelClose, scheduleClose, openPopupForText } = usePopupManager({
        setActivePopup,
        overlayRef,
        dimension: { width: 0, height: 0 },
    });

    const { codes, isExecutive, showCriticalCode } = useNoteCodes(workflowId);

    // Загрузка документа
    useEffect(() => {
        const container = wordContentRef.current;
        if (!container) return;
        setDocReady(false);
        setDocError(null);
        initialScrollDoneRef.current = false;
        container.innerHTML = '';

        let cancelled = false;
        fetch(url)
            .then(r => r.blob())
            .then(blob => {
                if (cancelled) return;
                return renderAsync(blob, container, undefined, {
                    ignoreWidth: true,
                    ignoreHeight: true,
                    breakPages: true,
                    inWrapper: true,
                });
            })
            .then(() => { if (!cancelled) setDocReady(true); })
            .catch(e => {
                console.error('docx-preview render error', e);
                if (!cancelled) setDocError('Не удалось открыть документ');
            });

        return () => { cancelled = true; };
    }, [url]);

    // Синхронизация highlights от родителя
    useEffect(() => {
        if (initialHighlights !== undefined) {
            setHighlights((prev) => {
                const keepFailed = keepUnresolvedFailedHighlights(prev, initialHighlights);
                return keepFailed.length === 0
                    ? initialHighlights
                    : [...initialHighlights, ...keepFailed];
            });
        }
    }, [initialHighlights]);

    // Прокрутка и flash-анимация на конкретный highlight по id
    const scrollToId = useCallback((id: string) => {
        flashElRef.current.forEach(p => p.classList.remove('highlight--selected'));

        const els = Array.from(document.querySelectorAll<HTMLElement>(`[data-highlight-id="${id}"]`));
        if (!els.length) return;

        const target = els.reduce((a, b) =>
            a.getBoundingClientRect().top <= b.getBoundingClientRect().top ? a : b
        );

        const cont = scrollRef.current;
        if (cont) {
            const c = cont.getBoundingClientRect();
            const t = target.getBoundingClientRect();
            const top = Math.max(0, cont.scrollTop + (t.top - c.top) - 80);
            cont.scrollTo({ top, behavior: 'smooth' });
        } else {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

        els.forEach(el => {
            el.classList.remove('highlight--selected');
            void el.offsetWidth;
            el.classList.add('highlight--selected');
        });
        flashElRef.current = els;
        target.addEventListener('animationend', () => {
            els.forEach(el => el.classList.remove('highlight--selected'));
            if (flashElRef.current === els) flashElRef.current = [];
        }, { once: true });
    }, []);

    const focusHighlight = useCallback((h: Highlight) => {
        setSelectedId(h.id);
        cancelClose();
        setActivePopup(null);
        scrollToId(h.id);
    }, [cancelClose, scrollToId]);

    // Авто-прокрутка при открытии (initialScrollToNoteId)
    useEffect(() => {
        if (!initialScrollToNoteId || initialScrollDoneRef.current) return;
        if (!docReady || highlights.length === 0) return;
        const h = highlights.find(hl => hl.id.startsWith(`${initialScrollToNoteId}-`));
        if (!h) return;
        initialScrollDoneRef.current = true;
        setTimeout(() => focusHighlight(h), 100);
    }, [docReady, highlights, initialScrollToNoteId, focusHighlight]);

    // Перерисовка highlight-оверлеев поверх Word-контента
    const restoreHighlights = useCallback(() => {
        const overlay = overlayRef.current;
        const content = wordContentRef.current;
        if (!overlay || !content) return;
        overlay.innerHTML = '';
        highlights
            .filter(h => h.type === 'text')
            .forEach(h => renderHighlight(h, content, overlay, openPopupForText, scheduleClose, focusHighlight));
    }, [highlights, openPopupForText, scheduleClose, focusHighlight]);

    useEffect(() => {
        if (docReady) restoreHighlights();
    }, [docReady, restoreHighlights]);

    // Пересчёт позиций при ресайзе окна (layout меняется — текст перетекает)
    useEffect(() => {
        const onResize = () => { if (docReady) restoreHighlights(); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [docReady, restoreHighlights]);

    // Создание текстовой заметки из выделения
    const handleMouseUp = useCallback(() => {
        if (!canAnnotate) return;
        if (!textMode) return;
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) return;
        const content = wordContentRef.current;
        if (!content || !content.contains(selection.anchorNode)) return;
        const range = selection.getRangeAt(0);
        const { startOffset, endOffset, text } = serializeSelection(content, range);
        if (!text.trim() || startOffset < 0 || endOffset < 0) return;
        selection.removeAllRanges();
        setPendingSelection({ startOffset, endOffset, text });
        setPendingComment('');
    }, [textMode]);

    // Highlight actions (инлайн-минимум без лишних polygon/rect/marker зависимостей)
    const isUnsavedLocal = (h: Highlight) => !h.detail?.noteId && !/^\d+-\d+$/.test(h.id);

    const persistList = useCallback((updated: Highlight[], failedIds: Set<string>) => {
        const result = onHighlightsChangeRef.current?.(updated);
        return Promise.resolve(result).catch(() => {
            const marked = highlightsRef.current.map((h) =>
                failedIds.has(h.id) ? { ...h, persistStatus: 'failed' as const } : h,
            );
            highlightsRef.current = marked;
            setHighlights(marked);
        });
    }, []);

    const updateHighlightNote = useCallback((id: string, note: string, isCritical?: boolean, codeId?: number) => {
        const prev = highlightsRef.current;
        const target = prev.find(h => h.id === id);
        if (!target) return;
        const noteChanged = (target.note ?? '') !== note;
        const criticalChanged = isCritical !== undefined && target.isCritical !== isCritical;
        const codeChanged = codeId !== undefined && target.codeId !== codeId;
        if (!noteChanged && !criticalChanged && !codeChanged) return;
        const noteId = target.detail?.noteId;
        const isSameNote = (h: Highlight) =>
            h.id === id || (noteId != null && h.detail?.noteId === noteId);
        const updated = prev.map(h =>
            isSameNote(h)
                ? {
                    ...h,
                    note,
                    isCritical: isCritical !== undefined ? isCritical : h.isCritical,
                    codeId: codeId !== undefined ? codeId : h.codeId,
                    persistStatus: isUnsavedLocal(h) ? 'saving' as const : h.persistStatus,
                    detail: h.detail ? { ...h.detail, titleHtml: note, isCritical: isCritical !== undefined ? isCritical : h.detail.isCritical } : h.detail,
                }
                : h
        );
        highlightsRef.current = updated;
        setHighlights(updated);
        void persistList(updated, new Set(updated.filter(isUnsavedLocal).map((h) => h.id)));
    }, [persistList]);

    const deleteHighlight = useCallback((id: string) => {
        const updated = highlightsRef.current.filter(h => h.id !== id);
        highlightsRef.current = updated;
        setHighlights(updated);
        void persistList(updated, new Set());
        setActivePopup(null);
    }, [persistList]);

    const retryPersistHighlight = useCallback((id: string) => {
        const target = highlightsRef.current.find((h) => h.id === id);
        if (!target || !isUnsavedLocal(target)) return;
        const updated = highlightsRef.current.map((h) =>
            h.id === id ? { ...h, persistStatus: 'saving' as const } : h,
        );
        highlightsRef.current = updated;
        setHighlights(updated);
        void persistList(updated, new Set([id]));
    }, [persistList]);

    const commitPendingSelection = useCallback(() => {
        if (!pendingSelection) return;
        const h: Highlight = {
            id: crypto.randomUUID(),
            type: 'text',
            pageNumber: 1,
            startOffset: pendingSelection.startOffset,
            endOffset: pendingSelection.endOffset,
            text: pendingSelection.text,
            note: pendingComment,
            isCritical: pendingIsCritical,
            codeId: pendingCodeId,
            createdAt: new Date().toISOString(),
            persistStatus: 'saving',
        };
        const updated = [...highlightsRef.current, h];
        highlightsRef.current = updated;
        setHighlights(updated);
        setPendingSelection(null);
        setPendingComment('');
        setPendingIsCritical(false);
        setPendingCodeId(undefined);
        void persistList(updated, new Set([h.id]));
    }, [pendingSelection, pendingComment, pendingIsCritical, pendingCodeId, persistList]);

    const cancelPending = useCallback(() => {
        setPendingSelection(null);
        setPendingComment('');
        setPendingIsCritical(false);
        setPendingCodeId(undefined);
        setLinkedNote(null);
    }, []);

    const commitEditModal = useCallback(() => {
        if (!editingHighlight) return;
        updateHighlightNote(editingHighlight.id, editingComment, editingIsCritical, editingCodeId);
        setEditingHighlight(null);
        setEditingComment('');
    }, [editingHighlight, editingComment, editingIsCritical, editingCodeId, updateHighlightNote]);

    const cancelEditModal = useCallback(() => {
        setEditingHighlight(null);
        setEditingComment('');
    }, []);

    const activeHighlight = activePopup ? highlights.find(h => h.id === activePopup.id) : undefined;

    const documentSearch = useWordViewerSearch({
        wordContentRef,
        searchOverlayRef,
        scrollRef,
        docReady,
    });

    useEffect(() => {
        documentSearch.setQuery('');
    }, [url]);

    const pendingAnnotation = useMemo(() => {
        if (!canAnnotate) return null;
        if (showLinkScreen || !pendingSelection) return null;
        return {
            title: 'Добавить замечание',
            quote: pendingSelection.text,
            geometry: {
                type: 'text' as const,
                page: 1,
                startOffset: pendingSelection.startOffset,
                endOffset: pendingSelection.endOffset,
                text: pendingSelection.text,
            } as HighlightGeometry,
            commit: commitPendingSelection,
        };
    }, [canAnnotate, showLinkScreen, pendingSelection, commitPendingSelection]);

    return (
        <div className={styles.viewer}>
            <div className={styles.pdfColumn} ref={wrapperRef}>
                <div className={styles.toolbar}>
                    {canAnnotate && <WordToolbar textMode={textMode} onToggleText={() => setTextMode(p => !p)} />}
                    <DocumentSearchBar
                        query={documentSearch.query}
                        onQueryChange={documentSearch.setQuery}
                        matchLabel={documentSearch.matchLabel}
                        matchCount={documentSearch.matchCount}
                        onPrev={documentSearch.goPrev}
                        onNext={documentSearch.goNext}
                        searching={documentSearch.searching}
                        inputRef={documentSearch.searchInputRef}
                    />
                </div>

                <div className={styles.scroll} ref={scrollRef}>
                    <div style={{ position: 'relative' }}>
                        <div
                            ref={wordContentRef}
                            style={{ cursor: textMode ? 'text' : undefined }}
                            onMouseUp={handleMouseUp}
                        />
                        <div
                            ref={searchOverlayRef}
                            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}
                        />
                        {/* Оверлей для highlight-дивов — позиционируется поверх контента */}
                        <div
                            ref={overlayRef}
                            style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 6 }}
                        />
                        {activePopup?.pinned && activeHighlight && (
                            <NotePopup
                                key={activePopup.id}
                                note={activeHighlight.note || ''}
                                x={activePopup.x}
                                y={activePopup.y}
                                yBottom={activePopup.yBottom}
                                autoFocus
                                onChange={note => updateHighlightNote(activePopup.id, note)}
                                onDelete={() => deleteHighlight(activePopup.id)}
                                onClose={() => setActivePopup(null)}
                                onFocus={() => {
                                    cancelClose();
                                    setActivePopup(prev => prev ? { ...prev, pinned: true } : prev);
                                }}
                                onMouseEnter={cancelClose}
                                onMouseLeave={scheduleClose}
                            />
                        )}
                    </div>
                </div>

                {!docReady && (
                    <div className={styles.openingOverlay}>
                        {docError ? (
                            <>
                                <span className={styles.openingError}>{docError}</span>
                                {onDownload && (
                                    <button type="button" className={styles.openingErrorButton} onClick={onDownload}>
                                        Скачать документ
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <i className={`pi pi-spinner pi-spin ${styles.openingSpinner}`} />
                                Открытие документа…
                            </>
                        )}
                    </div>
                )}

                {canAnnotate && <PendingAnnotationModals
                    editingHighlight={editingHighlight}
                    editingComment={editingComment}
                    onEditingCommentChange={setEditingComment}
                    onCommitEdit={commitEditModal}
                    onCancelEdit={cancelEditModal}
                    onDelete={deleteHighlight}
                    editingIsCritical={editingIsCritical}
                    onEditingIsCriticalChange={setEditingIsCritical}
                    editingCodeId={editingCodeId}
                    onEditingCodeIdChange={setEditingCodeId}
                    showLinkScreen={showLinkScreen}
                    linkedNote={linkedNote}
                    onLinkedNoteChange={setLinkedNote}
                    onShowLinkScreenChange={setShowLinkScreen}
                    workflowInstanceId={workflowInstanceId}
                    noteObjectId={noteObjectId}
                    workflowInstanceAttachmentId={workflowInstanceAttachmentId}
                    onLinkToExisting={onLinkToExisting}
                    pendingAnnotation={pendingAnnotation}
                    pendingComment={pendingComment}
                    onPendingCommentChange={setPendingComment}
                    pendingIsCritical={pendingIsCritical}
                    onPendingIsCriticalChange={setPendingIsCritical}
                    pendingCodeId={pendingCodeId}
                    onPendingCodeIdChange={setPendingCodeId}
                    onCancelPending={cancelPending}
                    codes={codes}
                    isExecutive={isExecutive}
                    showCriticalCode={showCriticalCode}
                />}
            </div>

            {!sidebarCollapsed && (
                <div className={styles.divider} onMouseDown={handleDividerDown} />
            )}

            {/* currentPage намеренно не передаётся: Word рендерится одним потоком
                без разбивки на страницы, фильтр «Эта страница» там бессмыслен */}
            <AnnotationsSidebar
                ref={sidebarRef}
                mode={mode}
                commentsPanel={commentsPanel}
                contractorPermissions={contractorPermissions}
                onContractorComplete={onContractorComplete}
                onContractorCancelCompletion={onContractorCancelCompletion}
                noteLinkHref={noteLinkHref}
                onCommentsCountChange={onCommentsCountChange}
                highlights={highlights}
                selectedId={selectedId}
                collapsed={sidebarCollapsed}
                onSelect={focusHighlight}
                onSaveNote={updateHighlightNote}
                onDelete={deleteHighlight}
                onRetryPersist={retryPersistHighlight}
                onDeleteNote={onDeleteNote}
                onToggle={() => setSidebarCollapsed(v => !v)}
                style={sidebarCollapsed ? undefined : { width: sidebarWidth }}
                noteActions={noteActions}
                onApproveNote={onApproveNote}
                onCancelNote={onCancelNote}
                onReturnToExpert={onReturnToExpert}
                onSendForApproval={onSendForApproval}
                onRevokeDecision={onRevokeDecision}
                onConfirmCompletion={onConfirmCompletion}
                onConfirmNotCompleted={onConfirmNotCompleted}
                onCancelByInitiator={onCancelByInitiator}
                onCancelCompletion={onCancelCompletion}
                onCompleteCompletedGIP={onCompleteCompletedGIP}
                onRequestAssignToExpert={onRequestAssignToExpert}
                codes={codes}
                isExecutive={isExecutive}
                showCriticalCode={showCriticalCode}
            />
        </div>
    );
}
